import React, { useState } from "react";
import { FormControlLabel, Checkbox, Box, Typography, CircularProgress } from "@mui/material";
import axios from "axios";
import useDialog from "../../../hooks/useDialog";

/**
 * WorldBankFundedCheckbox - Renders a checkbox for "პროექტი ფინანსდება მსოფლიო ბანკის მიერ გამოყოფილი სახსრებით"
 *
 * Visibility rules:
 * - Only visible to agency users (not banks)
 * - All agency users can see it
 *
 * Edit rules:
 * - Only გარემოსდაცვითი სპეციალისტი (agencyLevel === -1) and სოციალური რისკების სპეციალისტი (agencyLevel === -2) can toggle it
 * - Once the application is approved ("დადასტურებული"), the checkbox becomes disabled
 */
const WorldBankFundedCheckbox = ({ applicationId, applicationInformation, agencyLevel, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const { promptConfirm, showSnackbar } = useDialog();

  const isChecked = applicationInformation?.world_bank?.world_bank_funded === true;
  const isApproved = applicationInformation?.approved === true;
  const canEdit = (agencyLevel === -1 || agencyLevel === -2) && !isApproved;

  const handleChange = async () => {
    if (!canEdit) return;

    const confirmed = await promptConfirm({
      title: "დადასტურება",
      message: isChecked
        ? "დარწმუნებული ხართ, რომ გსურთ მონიშვნის გაუქმება?"
        : "დარწმუნებული ხართ, რომ გსურთ მონიშვნა?",
    });

    if (!confirmed) return;

    setLoading(true);
    try {
      const response = await axios.post(`/agency/new_universal/set_world_bank_funded/${applicationId}`);
      const newValue = response.data?.world_bank_funded ?? !isChecked;
      showSnackbar(response.data?.message || "ველი წარმატებით განახლდა", "success");
      if (onUpdate) {
        onUpdate({ ...applicationInformation, world_bank: { ...applicationInformation.world_bank, world_bank_funded: newValue } });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || "დაფიქსირდა შეცდომა";
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 1, mb: 1, pl: 1 }}>
      <FormControlLabel
        control={
          loading ? (
            <CircularProgress size={20} sx={{ ml: 1.4, mr: 1.4 }} />
          ) : (
            <Checkbox
              checked={isChecked}
              onChange={handleChange}
              disabled={!canEdit || loading}
              color="primary"
            />
          )
        }
        label={
          <Typography variant="body2" sx={{ fontWeight: isChecked ? "bold" : "normal" }}>
            პროექტი ფინანსდება მსოფლიო ბანკის მიერ გამოყოფილი სახსრებით
          </Typography>
        }
      />
    </Box>
  );
};

export default WorldBankFundedCheckbox;
